import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { usePlatform } from '../contexts/PlatformContext';
import { ArrowMark } from './Marks';
export interface PartnerSummary {
  slug: string;
  name: string;
  city: string;
  categories: string[];
  occasions: string[];
  count: number;
}

/** Every place that actually delivers catering, with what they are good for. */
export function usePartnerSummaries(): PartnerSummary[] {
  const {
    allCatering
  } = usePlatform();
  return useMemo(() => {
    const map = new Map<string, PartnerSummary>();
    allCatering.forEach(offer => {
      const existing = map.get(offer.placeSlug);
      if (existing) {
        if (!existing.categories.includes(offer.category)) existing.categories.push(offer.category);
        offer.occasions.forEach(o => {
          if (!existing.occasions.includes(o)) existing.occasions.push(o);
        });
        existing.count += 1;
        return;
      }
      map.set(offer.placeSlug, {
        slug: offer.placeSlug,
        name: offer.placeName,
        city: offer.placeCity,
        categories: [offer.category],
        occasions: [...offer.occasions],
        count: 1
      });
    });
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name, 'sv'));
  }, [allCatering]);
}
interface SelectProps {
  partners: PartnerSummary[];
  selected: string;
  onSelect: (slug: string) => void;
}

/**
 * Compact partner picker. A dropdown rather than chips — twelve names as
 * buttons wrapped to three rows and pushed the actual results off screen.
 */
export function PartnerSelect({
  partners,
  selected,
  onSelect
}: SelectProps) {
  return <div data-id="element-400" data-mp="2ivv1tonwwqpr">
      <label htmlFor="c-partner" className="mb-2 block text-[11px] font-bold uppercase tracking-wide3 text-black/45" data-id="element-401" data-mp="2iw4uxl497dzo">
        Verksamhet
      </label>
      <select id="c-partner" value={selected} onChange={e => onSelect(e.target.value)} className="max-w-[15rem] cursor-pointer truncate border-b border-black/25 bg-transparent pb-2 font-display text-2xl outline-none transition-colors duration-150 ease-out focus:border-rod" data-id="element-402" data-mp="olrep4cm1szj">
        <option value="" data-id="element-403" data-mp="1qzgtn8mpf4b4">Alla</option>
        {partners.map(p => <option key={p.slug} value={p.slug} data-id="element-404" data-mp="24yoz2j9gn8nr">
            {p.name}
          </option>)}
      </select>
    </div>;
}

/** What the chosen place is good for, shown above the results. */
export function PartnerDetail({
  partner
}: {
  partner: PartnerSummary;
}) {
  const rows: [string, string][] = [['Bra för', partner.occasions.join(' · ')], ['Levererar', partner.categories.join(' · ')], ['Sortiment', `${partner.count} alternativ`]];
  return <div className="mt-8 border-l-2 border-rod pl-5" data-id="element-405" data-mp="2jlrumke8378g">
      <p className="font-display text-xl uppercase leading-tight" data-id="element-406" data-mp="2jo4krku4wnwl">
        {partner.name} <span className="text-black/40" data-id="element-407" data-mp="2xm2ozxeqr3yd">· {partner.city}</span>
      </p>
      <dl className="mt-3 space-y-1.5 text-sm" data-id="element-408" data-mp="2snrd7h4vyjdi">
        {rows.map(([label, value]) => <div key={label} className="flex gap-3" data-id="element-409" data-mp="3c88bmwy79ixl">
            <dt className="w-24 shrink-0 text-[11px] font-bold uppercase tracking-wide2 text-black/45" data-id="element-410" data-mp="3c6oxbbw3thyg">
              {label}
            </dt>
            <dd data-id="element-411" data-mp="338l9ua1k6b40">{value}</dd>
          </div>)}
      </dl>
      <Link to={`/plats/${partner.slug}`} className="group mt-3 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide2 text-rod transition-colors duration-150 ease-out hover:text-black" data-id="element-412" data-mp="33a69ge2qck8q">
        Läs om dem
        <ArrowMark className="h-3 w-7 transition-transform duration-200 ease-out group-hover:translate-x-1" data-id="element-413" data-mp="2y9iittiey5f1" />
      </Link>
    </div>;
}